import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";
import { cx } from "./cx";

export const controlClass =
  "w-full min-h-11 rounded-lg border-2 border-ink bg-paper px-3 py-2 text-ink placeholder:text-faint " +
  "motion-safe:transition-[box-shadow,background-color] motion-safe:duration-300 focus:bg-card focus:shadow-hard-sm " +
  "disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-accent-strong aria-[invalid=true]:bg-accent-tint";

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  /** Receives the ids that tie the control to its label, hint and error. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/** Label + control + hint + error, wired with ids so screen readers read them together. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cx("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="font-medium">{label}</label>
      {children({ id, describedBy, invalid: !!error })}
      {hint && <span id={hintId} className="text-xs text-muted">{hint}</span>}
      {error && <span id={errId} className="text-xs font-bold text-link-hover">{error}</span>}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(controlClass, className)} {...rest} />;
}

/** A checkbox with its label beside it; clicking the text toggles it. */
export function Checkbox({ label, className, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: ReactNode }) {
  return (
    <label className={cx("flex cursor-pointer items-start gap-2.5", className)}>
      <span className="relative mt-px grid size-5 shrink-0 place-items-center">
        <input type="checkbox" {...rest}
          className="peer size-5 cursor-pointer appearance-none rounded-md border-2 border-ink bg-paper motion-safe:transition-colors checked:bg-accent-strong focus-visible:shadow-hard-sm" />
        <svg viewBox="0 0 16 16" aria-hidden="true" className="pointer-events-none absolute size-3.5 text-white opacity-0 peer-checked:opacity-100">
          <path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>{label}</span>
    </label>
  );
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx(controlClass, "bg-card", className)} {...rest} />;
}
